/**
 * Scenarios (/scenarios), under Docs › Requirements: every scenario of every
 * connected repository, one row each, with its id, title, state, repository and
 * feature file, and the change it belongs to (ADR-218, contract.md §6).
 *
 * SystemDox is the reading room here: GitHub is the record and the merge is the
 * approval, so a row's only actions are opening its steps and following its
 * links. The filters live in the address, so a list can be sent or come back to.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, ListChecks } from 'lucide-react';
import { getUserFriendlyErrorMessage } from '@puglieseweb/shared-observability';
import { Spinner } from '@systemdox/ui';
import { BreadcrumbBar } from '@/components/editor/BreadcrumbBar';
import { TagChip } from '@/components/changes/changeBadges';
import { CODE, TABLE, TD, TH, TableScroll } from '@/components/changes/StageSection';
import { changePath } from '@/lib/changes/model';
import { captureError } from '@/lib/sentry';
import {
  SCENARIO_STATES,
  countByState,
  filterRows,
  repositoriesOf,
  scenarioRows,
  type ScenarioRow,
} from '@/lib/scenarios/list';
import { docsApi } from '@/services/docsApi';

/** How many rows one "show more" adds: a workspace can hold thousands. */
const PAGE = 100;

const CONTROL = 'rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-800 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200';

function ReadFrom({ row }: { row: ScenarioRow }) {
  const pull = row.pullRequest;
  if (!pull) return null;
  return (
    <a
      href={pull.url}
      target="_blank"
      rel="noreferrer"
      data-read-from={pull.release ? 'release' : 'pull-request'}
      className="mt-0.5 block text-xs font-medium text-amber-800 hover:underline dark:text-amber-300"
    >
      {pull.release ? `not released · #${pull.number}` : `in pull request #${pull.number}`}
    </a>
  );
}

function Row({ row, search }: { row: ScenarioRow; search: string }) {
  const [open, setOpen] = useState(false);
  return (
    <tr data-scenario-row={row.id} data-row-key={row.key}>
      <td className={`${TD} whitespace-nowrap`}>
        {row.id ? <code className={CODE}>{row.id}</code> : <span data-no-id className="text-xs text-slate-400">no id</span>}
      </td>
      <td className={TD}>
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          data-open-steps
          className="flex w-full items-start gap-1 text-left text-slate-800 hover:underline dark:text-slate-200"
        >
          {open ? <ChevronDown className="mt-0.5 h-4 w-4 shrink-0" /> : <ChevronRight className="mt-0.5 h-4 w-4 shrink-0" />}
          <span>{row.title}</span>
        </button>
        {open && (
          <ul className="ml-5 mt-1 space-y-0.5 font-mono text-[11px] text-slate-500 dark:text-slate-400">
            {row.steps.map((step, i) => <li key={i} data-step className="whitespace-pre-wrap">{step}</li>)}
          </ul>
        )}
      </td>
      <td className={TD}><TagChip state={row.state} /></td>
      <td className={`${TD} min-w-[15rem] text-xs`}>
        <span className="block whitespace-nowrap">{row.repo}</span>
        <span className="block break-all text-slate-500 dark:text-slate-400" title={row.path}>{row.file}</span>
        <ReadFrom row={row} />
      </td>
      <td className={`${TD} text-xs`}>
        {row.changes.length === 0 ? (
          <span data-no-change className="text-slate-400">no change</span>
        ) : (
          row.changes.map((change) => (
            <Link key={change.slug} data-change-link to={{ pathname: changePath(change), search }} className="block text-violet-700 hover:underline dark:text-violet-300">
              {change.slug}
            </Link>
          ))
        )}
      </td>
    </tr>
  );
}

export default function ScenariosPage() {
  const { search } = useLocation();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState<ScenarioRow[] | null>(null);
  const [failed, setFailed] = useState('');
  const [shown, setShown] = useState(PAGE);

  useEffect(() => {
    let live = true;
    docsApi.listFeatureFiles().then(
      (files) => { if (live) setRows(scenarioRows(files)); },
      (error) => {
        captureError(error, { component: 'ScenariosPage' });
        if (live) setFailed(getUserFriendlyErrorMessage(error));
      },
    );
    return () => { live = false; };
  }, []);

  const filter = { repo: params.get('repo') ?? '', state: params.get('state') ?? '', q: params.get('q') ?? '' };
  const matching = useMemo(() => filterRows(rows ?? [], filter), [rows, filter.repo, filter.state, filter.q]);
  const counts = useMemo(() => countByState(rows ?? []), [rows]);
  const repositories = useMemo(() => repositoriesOf(rows ?? []), [rows]);

  // A change of filter is a new list, from its top. The rest of the address stays (?tenant on localhost).
  function setFilter(name: 'repo' | 'state' | 'q', value: string) {
    setShown(PAGE);
    const next = new URLSearchParams(params);
    if (value) next.set(name, value);
    else next.delete(name);
    setParams(next, { replace: true });
  }

  return (
    <div className="p-8">
      <BreadcrumbBar
        className="-mx-8 -mt-8 mb-6"
        crumbs={[
          { label: 'Docs', to: { pathname: '/docs', search } },
          { label: 'Requirements', to: { pathname: '/docs/requirements', search } },
          { label: 'Scenarios' },
        ]}
      />

      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500">
          <ListChecks className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Scenarios</h1>
          <p data-reflects className="mt-1 max-w-3xl text-sm text-slate-600 dark:text-slate-400">
            Every business scenario of your connected repositories. This list reflects each connected repository’s default branch and its open pull requests into preprod or the default branch; a scenario read from a pull request says so.
          </p>
        </div>
      </div>

      {failed && (
        <p role="alert" data-scenarios-failed className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-200">
          {failed}
        </p>
      )}

      {!rows && !failed && (
        <div className="flex justify-center py-12">
          <Spinner aria-label="Loading scenarios" />
        </div>
      )}

      {rows && rows.length === 0 && (
        <div data-no-scenarios className="rounded-xl border border-slate-200 bg-white p-8 text-center dark:border-slate-800 dark:bg-slate-900">
          <ListChecks className="mx-auto h-10 w-10 text-slate-300 dark:text-slate-700" />
          <h2 className="mt-4 font-semibold text-slate-900 dark:text-slate-100">No scenario yet</h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-slate-500 dark:text-slate-400">
            SystemDox reads scenarios only from connected repositories. Connect one under{' '}
            <Link to={{ pathname: '/settings', search }} className="font-medium text-violet-700 hover:underline dark:text-violet-300">Settings › GitHub</Link>.
          </p>
        </div>
      )}

      {rows && rows.length > 0 && (
        <>
          <p data-state-counts className="mb-3 flex flex-wrap gap-x-3 text-sm text-slate-600 dark:text-slate-400">
            <span>{rows.length} scenarios</span>
            {SCENARIO_STATES.filter(({ value }) => counts[value]).map(({ value, label }) => (
              <span key={value} data-count={value}>{counts[value]} {label.toLowerCase()}</span>
            ))}
          </p>

          <div className="mb-4 flex flex-wrap items-center gap-2">
            <label className="sr-only" htmlFor="scenario-repo">Repository</label>
            <select id="scenario-repo" data-filter="repo" className={CONTROL} value={filter.repo} onChange={(e) => setFilter('repo', e.target.value)}>
              <option value="">Every repository</option>
              {repositories.map((repo) => <option key={repo} value={repo}>{repo}</option>)}
            </select>
            <label className="sr-only" htmlFor="scenario-state">State</label>
            <select id="scenario-state" data-filter="state" className={CONTROL} value={filter.state} onChange={(e) => setFilter('state', e.target.value)}>
              <option value="">Every state</option>
              {SCENARIO_STATES.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
            </select>
            <label className="sr-only" htmlFor="scenario-search">Search by id or title</label>
            <input
              id="scenario-search"
              data-filter="q"
              type="search"
              placeholder="Search by id or title"
              className={`${CONTROL} w-full sm:w-auto sm:flex-1 sm:max-w-sm`}
              value={filter.q}
              onChange={(e) => setFilter('q', e.target.value)}
            />
            <span data-matching className="text-xs text-slate-500 dark:text-slate-400">{matching.length} shown</span>
          </div>

          {matching.length === 0 ? (
            <p className="py-12 text-center text-sm text-slate-400">No scenario matches these filters.</p>
          ) : (
            <TableScroll>
              <table className={`${TABLE} min-w-[48rem]`} data-scenarios>
                <thead>
                  <tr>
                    <th className={TH}>Id</th>
                    <th className={TH}>Scenario</th>
                    <th className={TH}>State</th>
                    <th className={TH}>Repository and file</th>
                    <th className={TH}>Change</th>
                  </tr>
                </thead>
                <tbody>
                  {matching.slice(0, shown).map((row) => <Row key={row.key} row={row} search={search} />)}
                </tbody>
              </table>
            </TableScroll>
          )}

          {matching.length > shown && (
            <button type="button" data-show-more onClick={() => setShown((n) => n + PAGE)} className="mt-4 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800">
              Show {Math.min(PAGE, matching.length - shown)} more of {matching.length - shown}
            </button>
          )}
        </>
      )}
    </div>
  );
}
